import React from 'react';

const STATUS_CLASS = {
  running: 'badge running',
  completed: 'badge ok',
  failed: 'badge fail',
  timeout: 'badge warn'
};

function fmt(ts) {
  return ts ? new Date(ts).toLocaleString() : '—';
}

/** Current/most-recent run detail with status badge and (placeholder) result. */
export default function RunStatus({ run }) {
  const status = (run.Status || '').toLowerCase();
  
  // Extract test results if available
  const testResults = run.Result?.testResults;
  const detailedResults = run.Result?.detailedResults;
  
  // Parse detailed test failures if available
  let failedTests = [];
  if (detailedResults && detailedResults.TestResults && Array.isArray(detailedResults.TestResults)) {
    // TestResults is an array of strings like:
    // "Fail: Test 12 - Scheduler service ran scheduled report and sent email - ..."
    // "Success: Test 1 - Achieved - ..."
    failedTests = detailedResults.TestResults
      .filter(testStr => testStr.startsWith('Fail:'))
      .map(testStr => {
        // Extract the test description after "Fail: "
        return testStr.substring(6); // Remove "Fail: " prefix
      });
  }
  
  return (
    <div className="card run-status">
      <div className="run-status-head">
        <span className={STATUS_CLASS[status] || 'badge'}>{status || 'unknown'}</span>
        <span className="muted">Run {run.RunId}</span>
      </div>
      {run.ScenarioName && (
        <div className="scenario-result-name">
          <span className="muted">Test name</span>
          <strong>{run.ScenarioName}</strong>
        </div>
      )}
      <dl className="kv">
        <div>
          <dt>Farm</dt>
          <dd>{run.Farm}</dd>
        </div>
        <div>
          <dt>Started</dt>
          <dd>{fmt(run.StartedAt)}</dd>
        </div>
        <div>
          <dt>Completed</dt>
          <dd>{fmt(run.CompletedAt)}</dd>
        </div>
        {run.TriggeredBy && (
          <div>
            <dt>Triggered by</dt>
            <dd>{run.TriggeredBy}</dd>
          </div>
        )}
        {run.ExecutionId && (
          <div>
            <dt>Execution ID</dt>
            <dd className="monospace">{run.ExecutionId}</dd>
          </div>
        )}
      </dl>
      
      {testResults ? (
        <div className={`test-results ${testResults.success ? 'success' : 'failure'}`}>
          <h4>{testResults.outcome}</h4>
          <div className="test-summary">
            <span className="test-metric">
              <strong>{testResults.passed}</strong> of <strong>{testResults.total}</strong> tests passed
            </span>
            {testResults.failures > 0 && (
              <span className="test-failures">
                <strong>{testResults.failures}</strong> {testResults.failures === 1 ? 'failure' : 'failures'}
              </span>
            )}
          </div>
          <p className="summary-text">{testResults.summary}</p>
          
          {failedTests.length > 0 && (
            <div className="failed-tests-list">
              <h5>Failed Tests:</h5>
              <ul>
                {failedTests.map((testName, idx) => (
                  <li key={idx}>{testName}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      ) : run.Result && run.Result.placeholder ? (
        <div className="result placeholder">
          Result gathering is not yet implemented. The scenario was triggered successfully.
        </div>
      ) : run.Result ? (
        <div className="result-details">
          <details>
            <summary>View raw result</summary>
            <pre className="result">{JSON.stringify(run.Result, null, 2)}</pre>
          </details>
        </div>
      ) : null}
    </div>
  );
}
